import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import clsx from 'clsx';
import { useContext, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import MaskedInput from 'react-text-mask';
import { twMerge } from 'tailwind-merge';

import QRCode from 'components/QRCode';
import { CheckmarkSVG } from 'components/icons/Checkmark';

import { ITrackEventProps } from 'typescriptDefs';

import { trackEvent } from 'helpers';
import { validPhoneRegex } from 'utils/constants';

import { Button } from './Button';
import { LocaleContext } from './Layout';
import { H4, P } from './Typography';

type Props = {
  intent: string;
  qrCodeLink: string;
  smsLink: string;
  trackEventProps: ITrackEventProps;
};

export const GetApp = ({ intent, qrCodeLink, smsLink, trackEventProps }: Props) => {
  const locale = useContext(LocaleContext);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  const setInvalidPhoneNumberError = () => {
    setError({ en: 'Please enter a valid phone number', fr: 'Veuillez entrer un numéro de téléphone valide' }[locale]);
  };

  const handleUpdatePhoneNumber = (newValue: string) => {
    setError(null);
    setPhoneNumber(newValue);
  };

  const {
    mutate: sendSMS,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: (body: { phone: string; link: string; intent: string }) =>
      axios.post(`${process.env.GATSBY_APP_GATEWAY}/1.0/notifications/sms/send_app_download_link`, body),
    onError: () =>
      setError(
        { en: 'Something went wrong. Please try again.', fr: 'Un problème est survenu. Veuillez essayer à nouveau.' }[
          locale
        ],
      ),
  });

  const handleSendSMS = () => {
    if (phoneNumber) {
      const numericOnlyPhoneNumber = phoneNumber.replace(/\D/g, '');
      sendSMS({ phone: numericOnlyPhoneNumber, link: smsLink, intent });
      trackEvent('Clicked', { ...trackEventProps, name: 'send-sms-to-download-app' });
    } else {
      setInvalidPhoneNumberError();
    }
  };

  return (
    <div className="flex flex-col gap-24 text-center">
      <P className="mb-16 text-grey-400 md:text-md">
        {
          {
            en: 'Scan the QR code to download or launch the app',
            fr: "Scannez le code QR pour télécharger ou lancer l'application",
          }[locale]
        }
      </P>
      <QRCode className="mx-auto mb-12" link={qrCodeLink} />
      {isSuccess ? (
        <LinkSent phoneNumber={phoneNumber} />
      ) : (
        <GetSMS
          error={error}
          isSmsPending={isPending}
          handleSendSMS={handleSendSMS}
          handleUpdatePhoneNumber={handleUpdatePhoneNumber}
          phoneNumber={phoneNumber}
          setInvalidPhoneNumberError={setInvalidPhoneNumberError}
        />
      )}
    </div>
  );
};

const LinkSent = ({ phoneNumber }: { phoneNumber: string }) => {
  const locale = useContext(LocaleContext);
  return (
    <>
      <H4 className="mb-4 leading-130 text-grey-500">{{ en: 'Link sent!', fr: 'Lien envoyé !' }[locale]}</H4>
      <P className="mb-16 text-grey-400 md:text-md">
        {
          {
            en: `You'll receive a text with a download link SMS at `,
            fr: `Vous recevrez un texto avec un lien de téléchargement au `,
          }[locale]
        }
        <span className="whitespace-nowrap">{phoneNumber}</span>
        {{ en: ` in a few moments`, fr: ` dans quelques instants` }[locale]}
      </P>
      <CheckmarkSVG className="mx-auto h-24 w-24 text-success-300" />
    </>
  );
};

const GetSMS = ({
  error,
  handleSendSMS,
  handleUpdatePhoneNumber,
  isSmsPending,
  phoneNumber,
  setInvalidPhoneNumberError,
}: {
  error: string | null;
  handleSendSMS: () => void;
  handleUpdatePhoneNumber: (s: string) => void;
  isSmsPending: boolean;
  phoneNumber: string;
  setInvalidPhoneNumberError: () => void;
}) => {
  const locale = useContext(LocaleContext);
  return (
    <div>
      <P className="mb-28 text-grey-400 md:text-md">
        {
          {
            en: 'or get a download link via SMS',
            fr: 'ou recevez un lien de téléchargement par texto',
          }[locale]
        }
      </P>
      <P className="mb-8 text-start text-grey-500 md:text-md">
        {
          {
            en: 'Mobile number',
            fr: 'Numéro de téléphone mobile',
          }[locale]
        }
      </P>
      <div className="relative mb-32">
        <MaskedInput
          className={twMerge(
            clsx(
              'w-full rounded-12 border border-grey-200 px-16 py-12 text-md text-grey-500 outline-none focus:border-primary-200',
              error && 'border-danger-200 placeholder:text-danger-300 focus:border-danger-300',
            ),
          )}
          type="tel"
          mask={['(', /[2-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
          placeholderChar={'\u2000'}
          keepCharPositions={false}
          placeholder={{ en: 'Enter your phone number', fr: 'Entrez votre numéro de téléphone' }[locale]}
          onChange={(e) => handleUpdatePhoneNumber(e.target.value)}
          onBlur={() => !validPhoneRegex.test(phoneNumber) && setInvalidPhoneNumberError()}
        />
        {error && <p className="absolute -bottom-20 left-20 text-xs text-danger-300">{error}</p>}
      </div>
      <Button disabled={!validPhoneRegex.test(phoneNumber)} onClick={handleSendSMS}>
        {isSmsPending ? (
          <div className="relative">
            <ClipLoader color="#FFFFFF" size="20px" />
          </div>
        ) : (
          { en: 'Send me a link', fr: 'Envoyez-moi un lien' }[locale]
        )}
      </Button>
    </div>
  );
};
